import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mic, Play, Pause, Download, Trash2, Radio, Loader2, ChevronDown, ChevronUp, Check, SkipBack, SkipForward, Repeat, Maximize2, Minimize2, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { MindMapNode, Podcast, ElevenLabsVoice } from '../types';
import { generatePodcast } from '../services/geminiService';
import { collection, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

interface VoiceOption {
    id: string;
    name: string;
    description: string;
    preview: string; // Vietnamese description
    provider: 'elevenlabs' | 'google';
    // language codes this voice sounds native in (undefined = general/multilingual)
    nativeFor?: string[];
    // language codes where this voice has known issues
    weakFor?: string[];
    weakWarning?: string;
    weakWarningVi?: string;
}

const BUILT_IN_VOICES: VoiceOption[] = [
    // ── Google Cloud TTS — native language models ───────────────────────────
    { id: 'google:vi-VN-Neural2-A', name: 'Mai', description: 'Vietnamese female · accurate tones', preview: 'Nữ Việt · chuẩn thanh điệu', provider: 'google', nativeFor: ['vi'] },
    { id: 'google:vi-VN-Neural2-D', name: 'Bảo', description: 'Vietnamese male · accurate tones', preview: 'Nam Việt · chuẩn thanh điệu', provider: 'google', nativeFor: ['vi'] },
    { id: 'google:en-US-Neural2-F', name: 'Sophie', description: 'English female · natural', preview: 'Nữ Anh · tự nhiên', provider: 'google', nativeFor: ['en'] },
    { id: 'google:en-US-Neural2-J', name: 'Henry', description: 'English male · natural', preview: 'Nam Anh · tự nhiên', provider: 'google', nativeFor: ['en'] },
    // ── ElevenLabs — high quality, multilingual ─────────────────────────────
    { id: 'JBFqnCBsd6RMkjVDRZzb', name: 'George', description: 'Deep & authoritative', preview: 'Trầm, quyền uy', provider: 'elevenlabs', weakFor: ['vi'], weakWarning: 'May mispronounce Vietnamese tones (ngã/hỏi)', weakWarningVi: 'Có thể đọc sai dấu ngã/hỏi' },
    { id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel', description: 'Calm, clear narrator', preview: 'Bình tĩnh, rõ ràng', provider: 'elevenlabs', weakFor: ['vi'], weakWarning: 'May mispronounce Vietnamese tones', weakWarningVi: 'Có thể đọc sai thanh điệu' },
    { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella', description: 'Soft & friendly', preview: 'Nhẹ nhàng, thân thiện', provider: 'elevenlabs', weakFor: ['vi'], weakWarning: 'May mispronounce Vietnamese tones', weakWarningVi: 'Có thể đọc sai thanh điệu' },
    { id: 'TxGEqnHWrfWFTfGW9XjX', name: 'Josh', description: 'Deep American voice', preview: 'Giọng trầm Mỹ', provider: 'elevenlabs', weakFor: ['vi'], weakWarning: 'May mispronounce Vietnamese tones', weakWarningVi: 'Có thể đọc sai thanh điệu' },
];

// Only languages ElevenLabs handles well (Latin-script European + Vietnamese + Indonesian)
const SUPPORTED_LANGUAGES = [
    { code: 'vi', flag: '🇻🇳', label: 'Tiếng Việt' },
    { code: 'en', flag: '🇺🇸', label: 'English' },
    { code: 'fr', flag: '🇫🇷', label: 'Français' },
    { code: 'es', flag: '🇪🇸', label: 'Español' },
    { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
    { code: 'pt', flag: '🇧🇷', label: 'Português' },
    { code: 'it', flag: '🇮🇹', label: 'Italiano' },
    { code: 'nl', flag: '🇳🇱', label: 'Nederlands' },
    { code: 'pl', flag: '🇵🇱', label: 'Polski' },
    { code: 'ru', flag: '🇷🇺', label: 'Русский' },
    { code: 'id', flag: '🇮🇩', label: 'Bahasa Indonesia' },
];

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

const formatTime = (s: number) => {
    if (!isFinite(s) || isNaN(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
};

const splitSentences = (text: string): string[] => {
    const raw = text.match(/[^.!?\n]+[.!?\n]+|[^.!?\n]+$/g) || [text];
    return raw.map(s => s.trim()).filter(s => s.length > 2);
};

const getSentenceIndex = (
    timestamps: Array<{ start: number; end: number }> | undefined,
    sentences: string[],
    currentTime: number,
): number => {
    // currentTime is media position in seconds — same unit as ElevenLabs/Google timestamps.
    // Do NOT multiply by speed: currentTime already reflects media position at any playback rate.
    if (timestamps && timestamps.length > 0 && timestamps[timestamps.length - 1].start > 0) {
        let best = 0;
        for (let i = 0; i < timestamps.length; i++) {
            if (currentTime >= timestamps[i].start) best = i;
            else break;
        }
        return best;
    }
    // Fallback WPM estimate for old podcasts without timestamps
    const WPM = 135;
    const currentWord = (currentTime * WPM) / 60;
    let cumWords = 0;
    for (let i = 0; i < sentences.length; i++) {
        cumWords += sentences[i].split(/\s+/).length;
        if (cumWords > currentWord) return i;
    }
    return sentences.length - 1;
};

// ─── FLOATING PLAYER ──────────────────────────────────────────────────────────

interface FloatingPodcastPlayerProps {
    podcast: Podcast;
    queue: Podcast[];
    language: string;
    onNavigate: (p: Podcast) => void;
    onClose: () => void;
}

export const FloatingPodcastPlayer: React.FC<FloatingPodcastPlayerProps> = ({ podcast, queue, language, onNavigate, onClose }) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const sentenceRefs = useRef<(HTMLSpanElement | null)[]>([]);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [speed, setSpeed] = useState(1);
    const [repeat, setRepeat] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [showScript, setShowScript] = useState(false);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);

    const sentences = React.useMemo(() => splitSentences(podcast.script || ''), [podcast.script]);
    const currentSentenceIdx = getSentenceIndex(podcast.sentenceTimestamps, sentences, currentTime);

    const currentIndex = queue.findIndex(p => p.id === podcast.id);
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < queue.length - 1;

    const navigatePrev = () => { if (hasPrev) onNavigate(queue[currentIndex - 1]); };
    const navigateNext = () => { if (hasNext) onNavigate(queue[currentIndex + 1]); };

    useEffect(() => {
        const audio = new Audio(podcast.audioUrl);
        audioRef.current = audio;
        audio.onloadedmetadata = () => setDuration(audio.duration);
        audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
        audio.onended = () => {
            if (repeat) {
                audio.currentTime = 0; audio.play();
            } else if (hasNext) {
                navigateNext();
            } else {
                setPlaying(false); setCurrentTime(0);
            }
        };
        setCurrentTime(0);
        audio.play().then(() => setPlaying(true)).catch(() => {});
        return () => { audio.pause(); audio.src = ''; };
    }, [podcast.audioUrl]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = speed;
    }, [speed]);

    useEffect(() => {
        if (showScript && sentenceRefs.current[currentSentenceIdx]) {
            sentenceRefs.current[currentSentenceIdx]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [currentSentenceIdx, showScript]);

    const togglePlay = () => {
        const a = audioRef.current;
        if (!a) return;
        if (playing) { a.pause(); setPlaying(false); }
        else { a.play(); setPlaying(true); }
    };

    const seek = (e: React.MouseEvent<HTMLDivElement>) => {
        const a = audioRef.current;
        if (!a || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        a.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * duration;
    };

    const skip = (sec: number) => {
        const a = audioRef.current;
        if (a) a.currentTime = Math.max(0, Math.min(duration, a.currentTime + sec));
    };

    const progress = duration > 0 ? currentTime / duration : 0;
    const isVi = language === 'vietnamese';

    // ── Mini bar (collapsed) ──────────────────────────────────────────────────
    if (!expanded) {
        return (
            <div className="fixed bottom-4 right-4 z-[300] w-80 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 overflow-hidden select-none">
                <div className="h-1 bg-gray-100 dark:bg-white/10 relative cursor-pointer" onClick={seek}>
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-pink-500 transition-none" style={{ width: `${progress * 100}%` }} />
                </div>
                <div className="flex items-center gap-3 px-4 py-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                        <Radio size={14} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(true)}>
                        <div className="text-xs font-semibold text-gray-900 dark:text-white truncate leading-tight">{podcast.title}</div>
                        <div className="text-[10px] text-gray-400 dark:text-white/50 mt-0.5">{formatTime(currentTime)} / {formatTime(duration)}{queue.length > 1 ? ` · ${currentIndex + 1}/${queue.length}` : ''}</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {queue.length > 1 && (
                            <button onClick={navigatePrev} disabled={!hasPrev} className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center transition-colors disabled:opacity-30">
                                <ChevronLeft size={14} className="text-gray-500 dark:text-white/60" />
                            </button>
                        )}
                        <button onClick={togglePlay} className="w-8 h-8 rounded-full bg-purple-100 dark:bg-white/10 hover:bg-purple-200 dark:hover:bg-white/20 flex items-center justify-center transition-colors">
                            {playing ? <Pause size={14} className="text-purple-600 dark:text-white" /> : <Play size={14} className="text-purple-600 dark:text-white ml-0.5" />}
                        </button>
                        {queue.length > 1 && (
                            <button onClick={navigateNext} disabled={!hasNext} className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center transition-colors disabled:opacity-30">
                                <ChevronRight size={14} className="text-gray-500 dark:text-white/60" />
                            </button>
                        )}
                        <button onClick={() => setExpanded(true)} className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center transition-colors">
                            <Maximize2 size={13} className="text-gray-400 dark:text-white/60" />
                        </button>
                        <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center transition-colors">
                            <X size={13} className="text-gray-400 dark:text-white/60" />
                        </button>
                    </div>
                </div>
                {/* Current sentence strip */}
                {sentences.length > 0 && (
                    <div className="px-4 pb-3 pt-0">
                        <p className="text-[11px] leading-relaxed text-purple-600 dark:text-purple-400 font-medium">
                            {sentences[currentSentenceIdx] || ''}
                        </p>
                    </div>
                )}
            </div>
        );
    }

    // ── Expanded player ───────────────────────────────────────────────────────
    return (
        <div className="fixed bottom-4 right-4 z-[300] w-80 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 overflow-hidden select-none">
            {/* Gradient header */}
            <div className="bg-gradient-to-br from-purple-600 to-pink-600 px-4 pt-4 pb-4">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                            <Radio size={14} className="text-white" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-white truncate leading-tight">{podcast.title}</div>
                            <div className="text-xs text-white/60 mt-0.5">
                                {podcast.nodeCount} {isVi ? 'ghi chú' : 'notes'}
                                {queue.length > 1 && <span className="ml-1.5">· {currentIndex + 1}/{queue.length}</span>}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <button onClick={() => setExpanded(false)} className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                            <Minimize2 size={13} className="text-white" />
                        </button>
                        <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                            <X size={13} className="text-white" />
                        </button>
                    </div>
                </div>
                {/* Progress bar */}
                <div onClick={seek} className="relative h-1.5 bg-white/25 rounded-full cursor-pointer group">
                    <div className="absolute inset-y-0 left-0 bg-white/80 rounded-full" style={{ width: `${progress * 100}%` }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity" style={{ left: `calc(${progress * 100}% - 6px)` }} />
                </div>
                <div className="flex justify-between text-[10px] text-white/60 mt-1.5">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                </div>
            </div>

            {/* Controls */}
            <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-3">
                    <button onClick={() => setRepeat(r => !r)} className={`p-1.5 rounded-lg transition-colors ${repeat ? 'text-purple-500' : 'text-gray-300 dark:text-white/30 hover:text-gray-500 dark:hover:text-white/60'}`}>
                        <Repeat size={16} />
                    </button>
                    {/* Prev track or skip-back */}
                    <button
                        onClick={hasPrev ? navigatePrev : () => skip(-10)}
                        className="p-1.5 text-gray-500 dark:text-white/60 hover:text-gray-800 dark:hover:text-white transition-colors relative group"
                        title={hasPrev ? (isVi ? 'Bài trước' : 'Previous') : '-10s'}
                    >
                        <SkipBack size={20} />
                        {!hasPrev && <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[8px] text-gray-400 dark:text-white/30 opacity-0 group-hover:opacity-100">10s</span>}
                    </button>
                    <button onClick={togglePlay} className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-transform">
                        {playing ? <Pause size={20} className="text-white" /> : <Play size={20} className="text-white ml-1" />}
                    </button>
                    {/* Next track or skip-forward */}
                    <button
                        onClick={hasNext ? navigateNext : () => skip(10)}
                        className="p-1.5 text-gray-500 dark:text-white/60 hover:text-gray-800 dark:hover:text-white transition-colors relative group"
                        title={hasNext ? (isVi ? 'Bài tiếp' : 'Next') : '+10s'}
                    >
                        <SkipForward size={20} />
                        {!hasNext && <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[8px] text-gray-400 dark:text-white/30 opacity-0 group-hover:opacity-100">10s</span>}
                    </button>
                    <div className="relative">
                        <button onClick={() => setShowSpeedMenu(s => !s)} className="px-2 py-1 rounded-lg text-xs font-bold text-gray-500 dark:text-white/50 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors border border-gray-200 dark:border-white/20 min-w-[38px]">
                            {speed}x
                        </button>
                        {showSpeedMenu && (
                            <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-white/10 overflow-hidden py-1 z-10 w-20">
                                {SPEEDS.map(sp => (
                                    <button key={sp} onClick={() => { setSpeed(sp); setShowSpeedMenu(false); }}
                                        className={`w-full px-3 py-1.5 text-xs text-left transition-colors ${speed === sp ? 'bg-purple-500 text-white' : 'text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/10'}`}>
                                        {sp}x
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Script toggle + download */}
                <div className="flex items-center justify-between border-t border-gray-100 dark:border-white/10 pt-3">
                    <button onClick={() => setShowScript(s => !s)} className={`flex items-center gap-1.5 text-[11px] font-medium transition-colors ${showScript ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/80'}`}>
                        <FileText size={12} />
                        {isVi ? 'Xem script' : 'Script'}
                        {showScript ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
                    </button>
                    <a href={podcast.audioUrl} download={`${podcast.title}.mp3`} className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/80 transition-colors">
                        <Download size={12} />
                        {isVi ? 'Tải xuống' : 'Download'}
                    </a>
                </div>

                {/* Synced script panel */}
                {showScript && (
                    <div className="mt-2 max-h-36 overflow-y-auto rounded-xl bg-gray-50 dark:bg-white/5 px-3 py-2.5">
                        <p className="text-[11px] leading-relaxed">
                            {sentences.map((s, i) => (
                                <span
                                    key={i}
                                    ref={el => { sentenceRefs.current[i] = el; }}
                                    className={`transition-colors duration-300 ${i === currentSentenceIdx
                                        ? 'text-purple-600 dark:text-purple-400 font-medium'
                                        : i < currentSentenceIdx
                                        ? 'text-gray-300 dark:text-white/20'
                                        : 'text-gray-400 dark:text-white/35'}`}
                                >
                                    {s}{' '}
                                </span>
                            ))}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── PODCAST HUB MODAL ────────────────────────────────────────────────────────

interface PodcastHubProps {
    nodes: MindMapNode[];
    notebookId: string;
    notebookTitle: string;
    userId: string;
    language: string;
    activePodcast: Podcast | null;
    onPlayPodcast: (p: Podcast) => void;
    onQueueChange: (podcasts: Podcast[]) => void;
    onClose: () => void;
}

type Tab = 'generate' | 'library';

export const PodcastHub: React.FC<PodcastHubProps> = ({
    nodes, notebookId, notebookTitle, userId, language, activePodcast, onPlayPodcast, onQueueChange, onClose,
}) => {
    const [tab, setTab] = useState<Tab>('generate');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [selectedVoiceId, setSelectedVoiceId] = useState(BUILT_IN_VOICES[0].id);
    const [audioLangCode, setAudioLangCode] = useState(language === 'vietnamese' ? 'vi' : 'en');
    const [podcastTitle, setPodcastTitle] = useState('');
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [podcasts, setPodcasts] = useState<Podcast[]>([]);
    const [loadingLibrary, setLoadingLibrary] = useState(false);
    const [libraryError, setLibraryError] = useState<string | null>(null);
    const [expandedScriptId, setExpandedScriptId] = useState<string | null>(null);
    const [nodeSearch, setNodeSearch] = useState('');
    const [showVoiceMenu, setShowVoiceMenu] = useState(false);
    const voiceMenuRef = useRef<HTMLDivElement>(null);

    const isVi = language === 'vietnamese';
    const selectedVoice = BUILT_IN_VOICES.find(v => v.id === selectedVoiceId) ?? BUILT_IN_VOICES[0];

    useEffect(() => {
        if (!showVoiceMenu) return;
        const handler = (e: MouseEvent) => {
            if (voiceMenuRef.current && !voiceMenuRef.current.contains(e.target as Node)) {
                setShowVoiceMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showVoiceMenu]);

    const displayNodes = nodes.filter(n =>
        (n.title || n.content) &&
        (!nodeSearch || (n.title || '').toLowerCase().includes(nodeSearch.toLowerCase()))
    );

    useEffect(() => { loadLibrary(); }, []);
    useEffect(() => { if (tab === 'library') loadLibrary(); }, [tab]);

    const loadLibrary = async () => {
        setLoadingLibrary(true);
        setLibraryError(null);
        try {
            const q = query(collection(db, `users/${userId}/podcasts`), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            const loaded = snap.docs.map(d => d.data() as Podcast);
            setPodcasts(loaded);
            onQueueChange(loaded);
        } catch (e: any) {
            setLibraryError(e?.message || 'Không tải được danh sách podcast');
        } finally {
            setLoadingLibrary(false);
        }
    };

    const toggleNode = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleGenerate = async () => {
        if (selectedIds.size === 0) return;
        setGenerating(true);
        setError(null);
        try {
            const podcast = await generatePodcast({
                nodeIds: Array.from(selectedIds),
                notebookId,
                title: podcastTitle || undefined,
                mode: 'manual',
                voiceId: selectedVoiceId,
                language: audioLangCode,
            });
            const updated = [podcast, ...podcasts];
            setPodcasts(updated);
            onQueueChange(updated);
            onPlayPodcast(podcast);
            setTab('library');
        } catch (e: any) {
            setError(e?.message || 'Failed to generate podcast');
        } finally {
            setGenerating(false);
        }
    };

    const handleDelete = async (podcast: Podcast) => {
        if (!window.confirm(isVi ? `Xóa "${podcast.title}"?` : `Delete "${podcast.title}"?`)) return;
        try {
            await deleteDoc(doc(db, `users/${userId}/podcasts/${podcast.id}`));
            const updated = podcasts.filter(p => p.id !== podcast.id);
            setPodcasts(updated);
            onQueueChange(updated);
        } catch (e) {
            console.error('Failed to delete podcast', e);
        }
    };

    const formatDate = (ts: number) => new Date(ts).toLocaleDateString(isVi ? 'vi-VN' : 'en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                            <Radio size={18} className="text-white" />
                        </div>
                        <div>
                            <h2 className="font-semibold text-gray-900 dark:text-white text-sm">StudyBoard Podcast Hub</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{notebookTitle}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                        <X size={18} className="text-gray-500" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
                    {(['generate', 'library'] as Tab[]).map(t => (
                        <button key={t} onClick={() => setTab(t)}
                            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${tab === t
                                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                        >
                            {t === 'generate' ? (isVi ? 'Tạo Podcast' : 'Generate') : (isVi ? 'Thư Viện' : 'Library')}
                            {t === 'library' && podcasts.length > 0 && (
                                <span className="ml-1.5 text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-full px-1.5 py-0.5">{podcasts.length}</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto">
                    {tab === 'generate' && (
                        <div className="p-6 space-y-5">
                            {/* Title */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    {isVi ? 'Tiêu đề (để trống để tự động tạo)' : 'Title (leave blank to auto-generate)'}
                                </label>
                                <input type="text" value={podcastTitle} onChange={e => setPodcastTitle(e.target.value)}
                                    placeholder={isVi ? 'Gemini sẽ tự tạo tiêu đề phù hợp...' : 'Gemini will generate a descriptive title...'}
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            {/* Audio language */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    {isVi ? 'Ngôn ngữ audio' : 'Audio language'}
                                </label>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                                    {SUPPORTED_LANGUAGES.map(lang => (
                                        <button key={lang.code} onClick={() => setAudioLangCode(lang.code)}
                                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs transition-colors truncate ${audioLangCode === lang.code
                                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
                                                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'}`}
                                        >
                                            <span>{lang.flag}</span>
                                            <span className="truncate">{lang.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Voice — compact dropdown */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    {isVi ? 'Giọng đọc' : 'Voice'}
                                </label>
                                <div className="relative" ref={voiceMenuRef}>
                                    <button
                                        onClick={() => setShowVoiceMenu(s => !s)}
                                        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-400 dark:hover:border-purple-500 transition-colors text-sm"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="font-medium text-gray-900 dark:text-white">{selectedVoice.name}</span>
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                                                selectedVoice.provider === 'google'
                                                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                                                    : 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400'
                                            }`}>
                                                {selectedVoice.provider === 'google' ? 'Google' : 'ElevenLabs'}
                                            </span>
                                            {selectedVoice.nativeFor?.includes(audioLangCode) && (
                                                <span className="text-[9px] text-green-600 dark:text-green-400">✓ {isVi ? 'Bản ngữ' : 'Native'}</span>
                                            )}
                                            {selectedVoice.weakFor?.includes(audioLangCode) && (
                                                <span className="text-[9px] text-amber-500">⚠</span>
                                            )}
                                        </div>
                                        <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${showVoiceMenu ? 'rotate-180' : ''}`} />
                                    </button>

                                    {showVoiceMenu && (
                                        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
                                            {BUILT_IN_VOICES.map(v => {
                                                const hasWarning = v.weakFor?.includes(audioLangCode);
                                                const isNative = v.nativeFor?.includes(audioLangCode);
                                                const warning = isVi ? v.weakWarningVi : v.weakWarning;
                                                const isSelected = v.id === selectedVoiceId;
                                                return (
                                                    <button key={v.id}
                                                        onClick={() => { setSelectedVoiceId(v.id); setShowVoiceMenu(false); }}
                                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${isSelected ? 'bg-purple-50 dark:bg-purple-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className={`text-xs font-semibold ${isSelected ? 'text-purple-700 dark:text-purple-300' : 'text-gray-800 dark:text-gray-200'}`}>
                                                                    {v.name}
                                                                </span>
                                                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                                                    v.provider === 'google'
                                                                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                                                                        : 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400'
                                                                }`}>
                                                                    {v.provider === 'google' ? 'Google' : 'EL'}
                                                                </span>
                                                                {isNative && <span className="text-[9px] text-green-600 dark:text-green-400">✓ {isVi ? 'Bản ngữ' : 'Native'}</span>}
                                                                {hasWarning && <span className="text-[9px] text-amber-500">⚠</span>}
                                                            </div>
                                                            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                                                                {isVi ? v.preview : v.description}
                                                                {hasWarning && warning && <span className="text-amber-500 ml-1">{warning}</span>}
                                                            </div>
                                                        </div>
                                                        {isSelected && <Check size={13} className="text-purple-500 flex-shrink-0" />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        {isVi ? `Chọn ghi chú (${selectedIds.size} đã chọn)` : `Select notes (${selectedIds.size} selected)`}
                                    </label>
                                    <div className="flex gap-2">
                                        <button onClick={() => setSelectedIds(new Set(displayNodes.map(n => n.id)))} className="text-xs text-purple-600 dark:text-purple-400 hover:underline">
                                            {isVi ? 'Tất cả' : 'All'}
                                        </button>
                                        <span className="text-gray-300 dark:text-gray-600">|</span>
                                        <button onClick={() => setSelectedIds(new Set())} className="text-xs text-gray-500 hover:underline">
                                            {isVi ? 'Bỏ chọn' : 'Clear'}
                                        </button>
                                    </div>
                                </div>
                                <input type="text" value={nodeSearch} onChange={e => setNodeSearch(e.target.value)}
                                    placeholder={isVi ? 'Tìm ghi chú...' : 'Search notes...'}
                                    className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 mb-2"
                                />
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 max-h-44 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                                    {displayNodes.length === 0 ? (
                                        <div className="px-3 py-4 text-xs text-center text-gray-400">{isVi ? 'Không tìm thấy ghi chú' : 'No notes found'}</div>
                                    ) : displayNodes.map(n => (
                                        <button key={n.id} onClick={() => toggleNode(n.id)}
                                            className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors text-xs ${selectedIds.has(n.id) ? 'bg-purple-50 dark:bg-purple-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                        >
                                            <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${selectedIds.has(n.id) ? 'bg-purple-500 border-purple-500' : 'border-gray-300 dark:border-gray-600'}`}>
                                                {selectedIds.has(n.id) && <Check size={10} className="text-white" />}
                                            </div>
                                            <span className="truncate text-gray-800 dark:text-gray-200">
                                                {n.title || (isVi ? 'Không có tiêu đề' : 'Untitled')}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {error && <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{error}</div>}
                        </div>
                    )}

                    {tab === 'library' && (
                        <div className="p-6">
                            {loadingLibrary ? (
                                <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-purple-500" /></div>
                            ) : libraryError ? (
                                <div className="text-center py-8">
                                    <p className="text-xs text-red-500 mb-3">{libraryError}</p>
                                    <button onClick={loadLibrary} className="text-xs text-purple-600 dark:text-purple-400 hover:underline">{isVi ? 'Thử lại' : 'Retry'}</button>
                                </div>
                            ) : podcasts.length === 0 ? (
                                <div className="text-center py-12">
                                    <Radio size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{isVi ? 'Chưa có podcast nào' : 'No podcasts yet'}</p>
                                    <button onClick={() => setTab('generate')} className="mt-3 text-xs text-purple-600 dark:text-purple-400 hover:underline">
                                        {isVi ? 'Tạo podcast đầu tiên →' : 'Generate your first podcast →'}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {podcasts.map((p, idx) => (
                                        <div key={p.id} className={`rounded-xl border overflow-hidden transition-colors ${activePodcast?.id === p.id ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                                            <div className="flex items-center gap-3 px-4 py-3">
                                                <div className="flex-shrink-0 text-center min-w-[20px]">
                                                    <span className="text-[10px] text-gray-400 dark:text-gray-600 font-mono">{idx + 1}</span>
                                                </div>
                                                <button onClick={() => onPlayPodcast(p)}
                                                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${activePodcast?.id === p.id ? 'bg-purple-500 hover:bg-purple-600' : 'bg-gray-100 dark:bg-gray-800 hover:bg-purple-100 dark:hover:bg-purple-900/40'}`}
                                                >
                                                    <Play size={15} className={`${activePodcast?.id === p.id ? 'text-white' : 'text-gray-500 dark:text-gray-400'} ml-0.5`} />
                                                </button>
                                                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onPlayPodcast(p)}>
                                                    <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{p.title}</div>
                                                    <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5">
                                                        <span>{formatDate(p.createdAt)}</span>
                                                        <span>·</span><span>~{p.durationEstimate} min</span>
                                                        <span>·</span><span>{p.nodeCount} {isVi ? 'ghi chú' : 'notes'}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <button onClick={() => setExpandedScriptId(expandedScriptId === p.id ? null : p.id)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400">
                                                        {expandedScriptId === p.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                                    </button>
                                                    <a href={p.audioUrl} download={`${p.title}.mp3`} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400">
                                                        <Download size={15} />
                                                    </a>
                                                    <button onClick={() => handleDelete(p)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-gray-400 hover:text-red-500">
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </div>
                                            {expandedScriptId === p.id && (
                                                <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 max-h-40 overflow-y-auto">
                                                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">{p.script}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {tab === 'generate' && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
                        <p className="text-xs text-gray-400">
                            {selectedIds.size === 0
                                ? (isVi ? 'Chọn ít nhất 1 ghi chú' : 'Select at least 1 note')
                                : (isVi ? `${selectedIds.size} ghi chú — ~2-3 phút audio` : `${selectedIds.size} notes — ~2-3 min audio`)}
                        </p>
                        <button onClick={handleGenerate} disabled={generating || selectedIds.size === 0}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {generating
                                ? <><Loader2 size={15} className="animate-spin" />{isVi ? 'Đang tạo...' : 'Generating...'}</>
                                : <><Mic size={15} />{isVi ? 'Tạo Podcast' : 'Generate Podcast'}</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
